import Image from "next/image";
import { useEffect, useMemo, useRef, useState } from "react";

import { useConfetti } from "@/context/confetti-context";
import { useStateContext } from "@/context/context-provider";

import KeyA from "./assets/key-a.webp";
import KeyD from "./assets/key-d.webp";
import KeyE from "./assets/key-e.webp";
import KeyM from "./assets/key-m.webp";
import KeyNumbers from "./assets/key-numbers.webp";
import KeyQ from "./assets/key-q.webp";
import KeyS from "./assets/key-s.webp";
import KeySpace from "./assets/key-space.webp";
import KeyW from "./assets/key-w.webp";
import KeyZ from "./assets/key-z.webp";
import styles from "./key.module.css";
const keys = {
    w: KeyW,
    a: KeyA,
    s: KeyS,
    d: KeyD,
    q: KeyQ,
    e: KeyE,
    z: KeyZ,
    m: KeyM,
    space: KeySpace,
    numbers: KeyNumbers,
};

interface KeyProps {
    keyType: keyof typeof keys;
    label?: string;
    className?: string;
}

export const Key = (props: KeyProps) => {
    const [clickCount, setClickCount] = useState(0);
    const [isPressed, setIsPressed] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const { triggerConfetti } = useConfetti();
    const { soundSettings } = useStateContext();

    const url = useMemo(() => keys[props.keyType], [props.keyType]);

    const playSound = () => {
        if (audioRef.current && !soundSettings.muted) {
            audioRef.current.currentTime = 0;
            audioRef.current.volume = soundSettings.volume * 0.05;
            void audioRef.current.play();
        }
    };

    const handleClick = () => {
        setIsPressed(true);
        playSound();

        const newCount = clickCount + 1;
        setClickCount(newCount);
        if (newCount === 10) {
            triggerConfetti();
            setClickCount(0);
        }
        setTimeout(() => setIsPressed(false), 100);
    };

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            const key = e.key.toLowerCase() === " " ? "space" : e.key.toLowerCase();
            if ((key === props.keyType || (props.keyType === "numbers" && "123456789".includes(e.key))) && !isPressed) {
                setIsPressed(true);
                playSound();
                const newCount = clickCount + 1;
                setClickCount(newCount);
                if (newCount === 10) {
                    triggerConfetti();
                    setClickCount(0);
                }
            }
        };

        const handleKeyUp = (e: KeyboardEvent) => {
            const key = e.key.toLowerCase() === " " ? "space" : e.key.toLowerCase();
            if (key === props.keyType || (props.keyType === "numbers" && "123456789".includes(e.key))) {
                setIsPressed(false);
            }
        };

        globalThis.addEventListener("keydown", handleKeyDown);
        globalThis.addEventListener("keyup", handleKeyUp);
        return () => {
            globalThis.removeEventListener("keydown", handleKeyDown);
            globalThis.removeEventListener("keyup", handleKeyUp);
        };
    }, [props.keyType, isPressed, clickCount, triggerConfetti, soundSettings]);

    return (
        <div className={`${styles.key} ${props.className || ""} ${isPressed ? styles.pressed : ""}`} onClick={handleClick}>
            <div className={styles.keyCap}>
                <Image height={48} src={url} alt={props.keyType + " key"} />
            </div>
            {props.label && <span className={styles.label}>{props.label}</span>}
            <audio ref={audioRef} src="/general/sounds/single-press-key-sound.mp3" preload="auto" />
        </div>
    );
};
